// هزینه‌های تعمیر و نگهداری ساختمان
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, Grid, Field, Lookup, MessageBox, splitLines, visibleCols, runReport,
} from './BuildingsShared';

const FORM = 'FRMBUILDINGREPAIR';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNNEWREQUEST', 'BTNBUILDINGFACILITIESREQUEST'];

const COLS = [
  { key: 'asker', label: 'درخواست کننده', wrap: true },
  { key: 'department', label: 'واحد اجرایی', wrap: true },
  { key: 'factor_no', label: 'فاکتور' },
  { key: 'event_date', label: 'تاریخ' },
  { key: 'amount_receiver', label: 'تعمیرکار', wrap: true },
  { key: 'amount', label: 'مبلغ', money: true },
  { key: 'remark', label: 'شرح تعمیرات', wrap: true },
  { key: 'request_no', label: 'شماره درخواست', num: true },
  { key: 'request_date', label: 'تاریخ درخواست' },
  { key: 'confirm_no', label: 'شماره تایید', num: true },
  { key: 'confirm_date', label: 'تاریخ تایید' },
  { key: 'hazineh_document_no', label: 'سند هزینه', num: true },
  { key: 'hazineh_document_date', label: 'تاریخ سند هزینه' },
  { key: 'user_group', label: 'گروه کاربری' },
];

const KEYS = ['factor_no', 'event_date', 'amount_receiver_id', 'amount_receiver', 'amount', 'remark', 'request_no',
  'request_date', 'confirm_no', 'confirm_date', 'asker_id', 'asker', 'department_id', 'department',
  'hazineh_document_no', 'hazineh_document_date'];
const pick = r => KEYS.reduce((o, k) => ({ ...o, [k]: r ? (r[k] ?? '') : '' }), {});

function Box({ title, children }) {
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 10px', flex: 1, minWidth: '220px' }}>
      <div style={ui.flabel}>{title}</div>
      <div style={{ display: 'flex', gap: '8px' }}>{children}</div>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '130px' }}>
      <label style={ui.mlabel}>{label}</label>
      <JalaliDatePicker value={value} onChange={onChange} placeholder={label} />
    </div>
  );
}

export default function BuildingRepair({ user, onBack, building, year }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(pick(null));
  const [formErr, setFormErr] = useState('');
  const [newReq, setNewReq] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(false);

  const load = useCallback(async (keepId) => {
    setLoading(true); setError('');
    const j = await API(`/${building.id}/repair/`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setRows(j.data);
    const hit = keepId ? j.data.find(r => r.id === keepId) : null;
    setSelected(hit || j.data[0] || null);
  }, [building]);

  useEffect(() => { if (fb.loaded && fb.open) load(); }, [fb.loaded, fb.open]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const has = !!selected;

  const openNew = async () => {
    const j = await API(`/repair/defaults/?year=${year}`);
    if (!j.ok) { showMsg(j.msg); return; }
    const s = selected || {};
    // مبلغ، شرح و شماره‌ها خالی می‌شوند؛ بقیهٔ فیلدها از ردیف جاری می‌مانند
    setForm({ ...pick(selected), amount: '', remark: '', hazineh_document_no: '', hazineh_document_date: '',
      ...j.data, asker_id: s.asker_id || 0, department_id: s.department_id || 0 });
    setFormErr(''); setNewReq(true); setModal('new');
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const openEdit = async () => {
    if (!selected) return;
    const j = await API(`/repair/${selected.id}/check-edit/`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(pick(selected)); setFormErr(''); setNewReq(false); setModal('edit');
  };
  const save = async () => {
    setFormErr('');
    const body = JSON.stringify({ ...form, building_id: building.id, year });
    const j = modal === 'new'
      ? await API('/repair/create/', { method: 'POST', body })
      : await API(`/repair/${selected.id}/update/`, { method: 'PUT', body });
    if (j.ok) {
      setModal(null); load(j.id);
      if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
    } else {
      setFormErr(j.msg);
      if (j.need_new_request) setNewReq(true);
    }
  };
  const newRequestNo = async () => {
    const j = await API(`/request-no/?year=${year}&request_date=${encodeURIComponent(form.request_date || '')}&confirm_date=${encodeURIComponent(form.confirm_date || '')}`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(f => ({ ...f, request_no: j.request_no, confirm_no: j.confirm_no }));
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const doDelete = async () => {
    setAsk(false);
    const j = await API(`/repair/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) load(); else showMsg(j.msg);
  };

  const pageTitle = 'هزینه های تعمیر و نگهداری ساختمان';
  const shown = visibleCols(COLS, rows);
  const toolbar = (
    <>
      {fb.open && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>＋ جدید</button>}
          {fb.can('BTNEDIT') && rows.length > 0 && <button onClick={openEdit} disabled={!has} style={{ ...ui.btnEdit, cursor: 'pointer' }}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && rows.length > 0 && <button onClick={() => has && setAsk(true)} disabled={!has} style={{ ...ui.btnDel, cursor: 'pointer' }}>✕ حذف</button>}
          {fb.can('BTNBUILDINGFACILITIESREQUEST') && has && (
            <button onClick={() => runReport('tadarokat.building_repair_request_one',
              { request_no: selected.request_no, request_date: selected.request_date }, showMsg)}
            style={{ ...ui.btnItems, cursor: 'pointer' }}>برگه درخواست</button>)}
        </div>
      )}
      <ExcelButton title={pageTitle} columns={shown} rows={rows} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={pageTitle}
        subtitle={`${building.remark || ''} — تدارکات`} toolbar={toolbar} error={error} loading={loading}>
        <Grid columns={shown} rows={rows} selectedId={selected && selected.id} onSelect={setSelected}
          loading={loading} empty="تعمیراتی ثبت نشده است" />
      </ListPage>

      {modal && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? 'فاکتور جدید' : 'ویرایش فاکتور'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {formErr && <div style={{ ...ui.errBox, whiteSpace: 'pre-line' }}>⚠️ {formErr}</div>}
              <div style={ui.row}>
                <Field label="شماره" value={form.factor_no} onChange={v => F('factor_no', v)} ltr />
                <DateField label="تاریخ" value={form.event_date} onChange={v => F('event_date', v)} />
                <Lookup label="تعمیرکار/فروشگاه" value={form.amount_receiver} endpoint="/lookup/amount-receivers/"
                  onPick={r => setForm(f => ({ ...f, amount_receiver_id: r.id, amount_receiver: r.title }))} />
                <Field label="مبلغ" value={form.amount} onChange={v => F('amount', v)} ltr />
              </div>
              <div style={ui.row}>
                <Field label="شرح تعمیرات/قطعات" value={form.remark} onChange={v => F('remark', v)} />
              </div>
              <div style={ui.row}>
                <Lookup label="درخواست کننده" value={form.asker} endpoint="/lookup/people/"
                  onPick={r => setForm(f => ({ ...f, asker_id: r.id, asker: r.title }))} />
                <Lookup label="واحد اجرایی" value={form.department} endpoint="/lookup/departments/"
                  onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.title }))} />
              </div>
              <div style={ui.row}>
                <Box title="درخواست">
                  <Field label="شماره" value={form.request_no} onChange={v => F('request_no', v)} ltr />
                  <DateField label="تاریخ" value={form.request_date} onChange={v => F('request_date', v)} />
                </Box>
                <Box title="تایید">
                  <Field label="شماره" value={form.confirm_no} onChange={v => F('confirm_no', v)} ltr />
                  <DateField label="تاریخ" value={form.confirm_date} onChange={v => F('confirm_date', v)} />
                </Box>
                <Box title="سند هزینه">
                  <Field label="شماره" value={form.hazineh_document_no || ''} disabled />
                  <Field label="تاریخ" value={form.hazineh_document_date || ''} disabled />
                </Box>
              </div>
            </div>
            <div style={ui.mFooter}>
              {newReq && fb.can('BTNOK') && <button onClick={newRequestNo} style={{ ...ui.btnItems, cursor: 'pointer' }}>شماره درخواست جدید</button>}
              {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
              {fb.can('BTNOK') && <button onClick={save} style={ui.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {ask && <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر"
        onConfirm={doDelete} onCancel={() => setAsk(false)} tone="danger" />}
      {msg && <MessageBox lines={msg} onClose={() => setMsg(null)} />}
    </>
  );
}
